import React, {useState, useEffect, useRef} from "react";
import {
  Box,
  Grid,
  Typography,
  Card,
  Chip,
  Button,
  Modal,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from "@mui/material";
import {StaticDatePicker, LocalizationProvider, PickersDay, DatePicker} from "@mui/x-date-pickers";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";
import {POST, GET, PATCH, DELETE} from "APIclient";
import CloseIcon from "@mui/icons-material/Close";
import {IconButton} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import properties from "constants/ApplicationConstants";
import PersonIcon from "@mui/icons-material/Person";
import {MobileTimePicker} from "@mui/x-date-pickers/MobileTimePicker";
import {Badge} from "@mui/material";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

class CustomAdapterDayjs extends AdapterDayjs {
  getTimezone = value => {
    if (!value || !dayjs.isDayjs(value) || !value.$x) {
      return dayjs.tz.guess();
    }
    return value.$x.$timezone || dayjs.tz.guess();
  };

  setTimezone = (value, timezone) => {
    if (!value || !dayjs.isDayjs(value)) {
      return dayjs().tz(timezone || dayjs.tz.guess());
    }
    return value.tz(timezone || dayjs.tz.guess(), true);
  };
}

const CustomCalendar = () => {
  const [calendarValue, setCalendarValue] = useState(dayjs().tz(dayjs.tz.guess()));
  const [conferenceValue, setConferenceValue] = useState(dayjs().tz(dayjs.tz.guess()));
  const [open, setOpen] = useState(false);
  const [formType, setFormType] = useState("event");
  const [selectedDate, setSelectedDate] = useState(dayjs().tz(dayjs.tz.guess()));
  const [isEditing, setIsEditing] = useState(false);
  const [events, setEvents] = useState({});
  const [conferenceBookings, setConferenceBookings] = useState({});
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [openSnabar, setSnakarOpen] = useState(false);
  const [snakColor, setSnakColor] = useState("");
  const [snakMessage, setSnakMessage] = useState("");
  const [openBookingList, setOpenBookingList] = useState(false);
  const [bookingList, setBookingList] = useState([]);
  const [viewOnly, setViewOnly] = useState(false);
  const userType = JSON.parse(localStorage.getItem("userType"));
  const currentEmpId = Number(window.sessionStorage.getItem("empId"));
  const empId = window.sessionStorage.getItem("empId");
  const empName = window.sessionStorage.getItem("EmpName");
  const [timeError, setTimeError] = useState("");

  // ✅ Guard: prevents API from firing more than once
  const hasFetched = useRef(false);

  const getNextRoundedTime = () => {
    const now = dayjs();
    const minutes = now.minute();
    const hour = now.hour();
    let nextTime;
    if (minutes === 0) {
      nextTime = now.hour(hour).minute(0).second(0);
    } else if (minutes <= 30) {
      nextTime = now.hour(hour).minute(30).second(0);
    } else {
      nextTime = now.add(1, "hour").hour(hour + 1).minute(0).second(0);
    }
    return nextTime;
  };

  const [formData, setFormData] = useState({
    empId: empId || 0,
    empName: empName,
    title: "",
    type: "HOLIDAY",
    startDate: dayjs().tz(dayjs.tz.guess()),
    endDate: dayjs().tz(dayjs.tz.guess()),
    conferenceType: "",
    startTime: getNextRoundedTime(),
    endTime: getNextRoundedTime().add(1, "hour"),
    noOfAttendee: "",
    calendarId: null
  });

  const normalizeType = className => {
    if (!className) return "EVENT";
    const lower = className.toLowerCase();
    if (lower.includes("holiday") || lower.includes("holidays")) return "HOLIDAY";
    if (lower.includes("training")) return "TRAINING";
    if (lower.includes("working")) return "WORKING";
    if (lower.includes("event")) return "EVENT";
    return "EVENT";
  };

  const getColorByType = type => {
    switch (type) {
      case "HOLIDAY": return "#5e35b1";
      case "TRAINING": return "#2D90D5";
      case "EVENT": return "#38C9F2";
      case "WORKING": return "#43a047";
      case "Conference Room": return "#00838f";
      case "Conference-II": return "#5e35b1";
      case "MULTI-UTILITY ROOM": return "#43a047";
      default: return undefined;
    }
  };

  // ✅ Standalone fetchEvents
  const fetchEvents = async () => {
    try {
      const response = await GET("admin/get-calendar-details");
      const data = response?.data?.data || [];
      localStorage.setItem("calendarEventData", JSON.stringify(data));
      const mappedEvents = {};
      data.forEach(event => {
        const start = dayjs(event.startDate).tz(dayjs.tz.guess());
        const end = dayjs(event.endDate).tz(dayjs.tz.guess());
        let currentDate = start;
        while (currentDate.isBefore(end) || currentDate.isSame(end, "day")) {
          const dateKey = currentDate.format("YYYY-MM-DD");
          const normalizedType = normalizeType(event.className);
          const normalizedTitle = event.title || normalizedType;
          if (!mappedEvents[dateKey]) mappedEvents[dateKey] = [];
          mappedEvents[dateKey].push({
            title: normalizedTitle,
            type: normalizedType,
            color:
              normalizedType === "HOLIDAY"
                ? getColorByType(normalizedType)
                : event.color || getColorByType(normalizedType),
            calendarId: event.calendarId,
            startDate: event.startDate,
            endDate: event.endDate
          });
          currentDate = currentDate.add(1, "day");
        }
      });
      setEvents(mappedEvents);
    } catch (err) {
      console.error("Error fetching events:", err);
      setSnakColor("error");
      setSnakMessage("Failed to fetch events");
      setSnakarOpen(true);
    }
  };

  // ✅ Standalone fetchConferenceBookings
  const fetchConferenceBookings = async () => {
    try {
      const response = await GET("admin/get-conference-details");
      const data = response?.data?.data || [];
      localStorage.setItem("conferenceBookingsData", JSON.stringify(data));
      const bookingsMap = {};
      data.forEach(booking => {
        const start = dayjs(booking.startDate).tz(dayjs.tz.guess());
        const end = dayjs(booking.endDate).tz(dayjs.tz.guess());
        let currentDate = start;
        while (currentDate.isBefore(end) || currentDate.isSame(end, "day")) {
          const dateStr = currentDate.format("YYYY-MM-DD");
          if (!bookingsMap[dateStr]) bookingsMap[dateStr] = [];
          bookingsMap[dateStr].push({
            conferenceId: booking.conferenceId,
            meetingTitle: booking.meetingTitle,
            conferenceType: booking.conferenceType,
            startTime: booking.startTime,
            endTime: booking.endTime,
            noOfAttendee: booking.noOfAttendee,
            empId: booking.empId,
            empName: booking.empName,
            startDate: booking.startDate,
            endDate: booking.endDate,
            color: getColorByType(booking.conferenceType)
          });
          currentDate = currentDate.add(1, "day");
        }
      });
      setConferenceBookings(bookingsMap);
    } catch (err) {
      console.error("Error fetching conference bookings:", err);
    }
  };

  // ✅ Single useEffect with hasFetched guard — APIs fire ONLY once on mount
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchEvents();
    fetchConferenceBookings();
  }, []);

  // ✅ Persist to localStorage when data changes
  useEffect(() => {
    localStorage.setItem("calendarEvents", JSON.stringify(events));
  }, [events]);

  useEffect(() => {
    localStorage.setItem("conferenceBookings", JSON.stringify(conferenceBookings));
  }, [conferenceBookings]);

  const checkForOverlappingBookings = (newBooking, isEditing = false, conferenceId = null) => {
    const bookingData = JSON.parse(localStorage.getItem("conferenceBookingsData") || "[]");
    const newRoom = newBooking.conferenceType;
    const newStartDate = dayjs(newBooking.startDate);
    const newEndDate = dayjs(newBooking.endDate);
    for (const booking of bookingData) {
      if (booking.conferenceType !== newRoom) continue;
      if (isEditing && booking.conferenceId === conferenceId) continue;
      const existingStartDate = dayjs(booking.startDate);
      const existingEndDate = dayjs(booking.endDate);
      if (newEndDate.isBefore(existingStartDate) || newStartDate.isAfter(existingEndDate)) continue;
      let currentDate = newStartDate.isAfter(existingStartDate) ? newStartDate : existingStartDate;
      const lastDate = newEndDate.isBefore(existingEndDate) ? newEndDate : existingEndDate;
      while (currentDate.isBefore(lastDate) || currentDate.isSame(lastDate, "day")) {
        const newStartTime = dayjs(`${currentDate.format("YYYY-MM-DD")}T${newBooking.startTime}`);
        const newEndTime = dayjs(`${currentDate.format("YYYY-MM-DD")}T${newBooking.endTime}`);
        const existingStartTime = dayjs(`${currentDate.format("YYYY-MM-DD")}T${booking.startTime}`);
        const existingEndTime = dayjs(`${currentDate.format("YYYY-MM-DD")}T${booking.endTime}`);
        if (!(newEndTime.isBefore(existingStartTime) || newStartTime.isAfter(existingEndTime))) {
          return true;
        }
        currentDate = currentDate.add(1, "day");
      }
    }
    return false;
  };

  const handleShowBookingList = (date, bookings) => {
    setBookingList(bookings);
    setOpenBookingList(true);
  };

  const handleViewBooking = (booking, readonly = false) => {
    setIsEditing(true);
    setViewOnly(!!readonly);
    setFormType("conference");
    setFormData({
      ...formData,
      title: booking.meetingTitle,
      conferenceType: booking.conferenceType,
      startDate: dayjs(booking.startDate).tz(dayjs.tz.guess()),
      endDate: dayjs(booking.endDate).tz(dayjs.tz.guess()),
      startTime: dayjs(`${booking.startDate}T${booking.startTime}`).tz(dayjs.tz.guess()),
      endTime: dayjs(`${booking.endDate}T${booking.endTime}`).tz(dayjs.tz.guess()),
      noOfAttendee: booking.noOfAttendee,
      empId: Number(booking.empId) || 0,
      empName: booking.empName,
      conferenceId: booking.conferenceId
    });
    setOpenBookingList(false);
    setOpen(true);
  };

  const handleDeleteClick = booking => {
    setIsEditing(true);
    setViewOnly(true);
    setFormType("conference");
    setFormData({
      ...formData,
      title: booking.meetingTitle,
      conferenceType: booking.conferenceType,
      startDate: dayjs(booking.startDate).tz(dayjs.tz.guess()),
      endDate: dayjs(booking.endDate).tz(dayjs.tz.guess()),
      startTime: dayjs(`${booking.startDate}T${booking.startTime}`).tz(dayjs.tz.guess()),
      endTime: dayjs(`${booking.endDate}T${booking.endTime}`).tz(dayjs.tz.guess()),
      noOfAttendee: booking.noOfAttendee,
      empId: Number(booking.empId) || 0,
      empName: booking.empName,
      conferenceId: booking.conferenceId
    });
    setOpenBookingList(false);
    setConfirmDeleteOpen(true);
  };

  const handleAddNewBooking = () => {
    setIsEditing(false);
    setViewOnly(false);
    const todayTz = dayjs().tz(dayjs.tz.guess());
    if (selectedDate && dayjs(selectedDate).isBefore(todayTz, "day")) {
      setSnakColor("error");
      setSnakMessage("Cannot create a booking for a past date");
      setSnakarOpen(true);
      setOpenBookingList(false);
      return;
    }
    setFormData({
      empId: empId || 0,
      empName: empName,
      title: "",
      type: "HOLIDAY",
      startDate: selectedDate,
      endDate: selectedDate,
      conferenceType: "",
      startTime: getNextRoundedTime(),
      endTime: getNextRoundedTime().add(1, "hour"),
      noOfAttendee: "",
      calendarId: null
    });
    setOpenBookingList(false);
    setOpen(true);
  };

  const handleDayClick = (date, type) => {
    setSelectedDate(date.tz(dayjs.tz.guess()));
    setFormType(type);
    if (userType !== "Admin" && type === "event") return;
    const defaultData = {
      empId: empId || 0,
      empName: empName,
      title: "",
      type: "HOLIDAY",
      startDate: date.tz(dayjs.tz.guess()),
      endDate: date.tz(dayjs.tz.guess()),
      conferenceType: "",
      startTime: getNextRoundedTime(),
      endTime: getNextRoundedTime().add(1, "hour"),
      noOfAttendee: "",
      calendarId: null
    };

    if (type === "conference") {
      const today = dayjs().tz(dayjs.tz.guess()).startOf("day");
      const bookingData = JSON.parse(localStorage.getItem("conferenceBookingsData") || "[]");
      const matchedBookings = bookingData.filter(b => {
        const start = dayjs(b.startDate).tz(dayjs.tz.guess());
        const end = dayjs(b.endDate).tz(dayjs.tz.guess());
        return (
          date.isSame(start, "day") ||
          date.isSame(end, "day") ||
          (date.isAfter(start) && date.isBefore(end))
        );
      });
      if (date.isBefore(today, "day")) {
        if (matchedBookings.length > 0) {
          handleShowBookingList(date, matchedBookings);
        } else {
          setOpen(false);
          return;
        }
      } else {
        if (matchedBookings.length > 0) {
          handleShowBookingList(date, matchedBookings);
        } else {
          setIsEditing(false);
          setViewOnly(false);
          setFormData(defaultData);
          setOpen(true);
        }
      }
    } else {
      if (date.isBefore(dayjs().tz(dayjs.tz.guess()), "day")) {
        setOpen(false);
        return;
      }
      const bookingData = JSON.parse(localStorage.getItem("calendarEventData") || "[]");
      const matchedBooking = bookingData.find(b => {
        const start = dayjs(b.startDate).tz(dayjs.tz.guess());
        const end = dayjs(b.endDate).tz(dayjs.tz.guess());
        return (
          date.isSame(start, "day") ||
          date.isSame(end, "day") ||
          (date.isAfter(start) && date.isBefore(end))
        );
      });
      if (matchedBooking) {
        setIsEditing(true);
        setViewOnly(false);
        setFormData({
          title: matchedBooking.title || "",
          type: normalizeType(matchedBooking.className),
          startDate: dayjs(matchedBooking.startDate).tz(dayjs.tz.guess()),
          endDate: dayjs(matchedBooking.endDate).tz(dayjs.tz.guess()),
          conferenceType: "",
          startTime: getNextRoundedTime(),
          endTime: getNextRoundedTime().add(1, "hour"),
          noOfAttendee: "",
          calendarId: matchedBooking.calendarId
        });
        setOpen(true);
      } else {
        setIsEditing(false);
        setViewOnly(false);
        setFormData(defaultData);
        setOpen(true);
      }
    }
  };

  const handleSnabarClose = () => setSnakarOpen(false);

  const handleSubmit = async () => {
    if (!formData.startDate || !formData.endDate) {
      setSnakColor("error");
      setSnakMessage("Start or end date is missing.");
      setSnakarOpen(true);
      return;
    }
    if (!formData.title) {
      setSnakColor("error");
      setSnakMessage("Title is missing.");
      setSnakarOpen(true);
      return;
    }

    if (formType === "event") {
      const payload = {
        eventType: formData.type.toLowerCase(),
        title: formData.title,
        startDate: formData.startDate.format("YYYY-MM-DD"),
        endDate: formData.endDate.format("YYYY-MM-DD"),
        calendarId: formData.calendarId
      };
      try {
        let response;
        if (isEditing) {
          response = await PATCH("admin/edit-events", payload);
        } else {
          response = await POST("admin/add-calender-events", payload);
        }
        if (response?.data?.statusCode === 200 && response?.data?.status === "OK") {
          setSnakColor("success");
          setSnakMessage(isEditing ? "Event updated successfully" : "Event added successfully");
          setSnakarOpen(true);
          const updatedBookings = JSON.parse(localStorage.getItem("calendarEventData") || "[]");
          if (isEditing) {
            const index = updatedBookings.findIndex(b => b.calendarId === formData.calendarId);
            if (index !== -1) {
              updatedBookings[index] = {
                ...updatedBookings[index],
                ...payload,
                className: formData.type.toUpperCase()
              };
            }
          } else {
            updatedBookings.push({
              ...payload,
              calendarId: response?.data?.data?.calendarId,
              className: formData.type.toUpperCase()
            });
          }
          localStorage.setItem("calendarEventData", JSON.stringify(updatedBookings));
          await fetchEvents();
          setOpen(false);
        } else {
          throw new Error(response?.data?.message || "API response not OK");
        }
      } catch (err) {
        console.error("Error processing event:", err);
        setSnakColor("error");
        setSnakMessage(err.message || "Error while processing event");
        setSnakarOpen(true);
      }
    } else if (formType === "conference") {
      if (!formData.startTime || !formData.endTime || !formData.conferenceType) {
        setSnakColor("error");
        setSnakMessage("Start time, end time, or conference room is missing.");
        setSnakarOpen(true);
        return;
      }
      const newBooking = {
        startDate: formData.startDate.format("YYYY-MM-DD"),
        endDate: formData.endDate.format("YYYY-MM-DD"),
        startTime: formData.startTime.format("HH:mm:ss"),
        endTime: formData.endTime.format("HH:mm:ss"),
        conferenceType: formData.conferenceType
      };
      if (checkForOverlappingBookings(newBooking, isEditing, formData.conferenceId)) {
        setSnakColor("error");
        setSnakMessage(
          `The selected conference room (${formData.conferenceType}) is already booked for the specified time.`
        );
        setSnakarOpen(true);
        return;
      }
      const payload = {
        empId: formData.empId || 0,
        empName: formData.empName,
        meetingTitle: formData.title,
        startDate: formData.startDate.format("YYYY-MM-DD"),
        endDate: formData.endDate.format("YYYY-MM-DD"),
        startTime: formData.startTime.format("HH:mm:ss"),
        endTime: formData.endTime.format("HH:mm:ss"),
        conferenceType: formData.conferenceType,
        noOfAttendee: formData.noOfAttendee
      };
      try {
        let response;
        if (isEditing) {
          payload.conferenceId = formData.conferenceId;
          response = await PATCH("admin/edit-conference-booking", payload);
        } else {
          response = await POST("admin/add-conference-booking", payload);
        }
        if (response?.data?.statusCode === 200 && response?.data?.status === "OK") {
          setSnakColor("success");
          setSnakMessage("Conference booking updated successfully");
          setSnakarOpen(true);
          const updatedBookings = JSON.parse(
            localStorage.getItem("conferenceBookingsData") || "[]"
          );
          if (isEditing) {
            const index = updatedBookings.findIndex(
              b => b.conferenceId === formData.conferenceId
            );
            if (index !== -1) {
              updatedBookings[index] = {...updatedBookings[index], ...payload};
            }
          } else {
            updatedBookings.push({
              ...payload,
              conferenceId: response?.data?.data?.conferenceId
            });
          }
          localStorage.setItem("conferenceBookingsData", JSON.stringify(updatedBookings));
          await fetchConferenceBookings();
          setOpen(false);
        } else {
          throw new Error(response?.data?.message || "API response not OK");
        }
      } catch (err) {
        console.error("Error processing conference booking:", err);
        setSnakColor("error");
        setSnakMessage(err.message || "Error while booking conference");
        setSnakarOpen(true);
      }
    }
  };

  const canEditOrDelete = booking => {
    if (userType === "Admin") return true;
    if (!booking || !booking.conferenceId) return true;
    return Number(booking.empId) === Number(currentEmpId);
  };

  const handleDeleteBooking = async () => {
    if (formType === "event") {
      try {
        const response = await DELETE(`admin/delete-events/${formData.calendarId}`);
        if (response?.data?.statusCode === 200 && response?.data?.status === "OK") {
          setSnakColor("success");
          setSnakMessage("Event deleted successfully");
          setSnakarOpen(true);
          const updatedBookings = JSON.parse(
            localStorage.getItem("calendarEventData") || "[]"
          ).filter(b => b.calendarId !== formData.calendarId);
          localStorage.setItem("calendarEventData", JSON.stringify(updatedBookings));
          await fetchEvents();
          setConfirmDeleteOpen(false);
          setOpen(false);
        } else {
          throw new Error(response?.data?.message || "API response not OK");
        }
      } catch (err) {
        setSnakColor("error");
        setSnakMessage(err.message || "Error while deleting event");
        setSnakarOpen(true);
      }
    } else if (formType === "conference") {
      try {
        const response = await DELETE(
          `admin/delete-conference-booking/${formData.conferenceId}`
        );
        if (response?.data?.statusCode === 200 && response?.data?.status === "OK") {
          setSnakColor("success");
          setSnakMessage("Conference booking deleted successfully");
          setSnakarOpen(true);
          const updatedBookings = JSON.parse(
            localStorage.getItem("conferenceBookingsData") || "[]"
          ).filter(b => b.conferenceId !== formData.conferenceId);
          localStorage.setItem("conferenceBookingsData", JSON.stringify(updatedBookings));
          await fetchConferenceBookings();
          setConfirmDeleteOpen(false);
          setOpen(false);
        } else {
          throw new Error(response?.data?.message || "API response not OK");
        }
      } catch (err) {
        setSnakColor("error");
        setSnakMessage(err.message || "Error while deleting conference booking");
        setSnakarOpen(true);
      }
    }
  };

  const renderCard = ({title, buttonLabel, chips, value, onChange, type}) => (
    <Card
      elevation={0}
      sx={{
        bgcolor: "#fff",
        minHeight: "100%",
        boxShadow: "0 6px 18px rgba(18,38,63,0.12)",
        border: "1px solid #e6eef7",
        overflow: "auto"
      }}
    >
      <Box
        sx={{
          background: "linear-gradient(90deg, #1976d2, #42a5f5)",
          px: 1.5,
          py: 1,
          borderTopLeftRadius: "16px",
          borderTopRightRadius: "16px"
        }}
      >
        <Typography variant="h6" fontWeight="600" color="#fff" letterSpacing={0.5}>
          {title}
        </Typography>
      </Box>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        px={2}
        py={1.5}
        flexWrap="wrap"
        gap={2}
        borderBottom="1px solid #eee"
      >
        <Box display="flex" flexWrap="wrap" gap={1}>
          {chips.map((chip, i) => (
            <Chip
              key={i}
              label={chip.label}
              size="small"
              sx={{
                backgroundColor: chip.bg,
                color: "#fff",
                fontWeight: 600,
                fontSize: "0.72rem",
                px: 0.75,
                borderRadius: 1.5
              }}
            />
          ))}
        </Box>
        {buttonLabel && (
          <Button
            variant="contained"
            size="small"
            sx={{
              textTransform: "none",
              background: "#ffffff",
              color: "#1976d2",
              border: "1px solid #1976d2",
              fontWeight: 500,
              px: 2.5,
              borderRadius: 2,
              "&:hover": {background: "#1976d2", color: "#fff"}
            }}
            onClick={() => onChange(dayjs().tz(dayjs.tz.guess()))}
          >
            {buttonLabel}
          </Button>
        )}
      </Box>

      <Box px={1} py={1}>
        <LocalizationProvider dateAdapter={CustomAdapterDayjs}>
          <StaticDatePicker
            value={value}
            onChange={newValue => {
              if (newValue) onChange(dayjs(newValue).tz(dayjs.tz.guess()));
            }}
            displayStaticWrapperAs="desktop"
            showDaysOutsideCurrentMonth
            reduceAnimations
            firstDayOfWeek={0}
            dayOfWeekFormatter={day =>
              ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][day.day()]
            }
            sx={{
              width: "100%",
              "& .MuiDateCalendar-root": {width: "100%", overflow: "visible", mb: 12},
              "& .MuiPickersCalendarHeader-root": {px: 1, mx: 0},
              "& .MuiPickersSlideTransition-root": {mx: 0, overflow: "visible"},
              "& .MuiDayCalendar-header": {mx: 0, opacity: 0.85, fontWeight: 600},
              "& .MuiDayCalendar-weekDayLabel": {
                width: "clamp(36px, 6.5vw, 58px)",
                display: "inline-flex",
                justifyContent: "center",
                mx: 0
              },
              "& .MuiDayCalendar-weekContainer": {
                mx: 0,
                justifyContent: "center",
                gap: "4px"
              },
              "& .MuiPickersDay-root": {borderRadius: 2, mx: 0},
              "& .MuiDayCalendar-monthContainer": {px: 0}
            }}
            slots={{
              day: props => {
                const dateNum = props?.day.format("YYYY-MM-DD");
                const map = type === "event" ? events : conferenceBookings;
                const eventData = map[dateNum]?.[0];
                const color =
                  eventData?.color ||
                  getColorByType(eventData?.type) ||
                  getColorByType(eventData?.conferenceType);
                const titleText =
                  type === "event"
                    ? eventData?.title || ""
                    : eventData?.meetingTitle || "";
                const countForDay = map[dateNum]?.length || 0;
                const tooltipText = titleText
                  ? `${titleText}${
                      type === "conference" && countForDay > 1
                        ? ` (+${countForDay - 1} more)`
                        : ""
                    }`
                  : "No Event";

                return (
                  <Tooltip
                    title={tooltipText}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: color || "rgb(0 0 0 / 0%)",
                          color: "#fff",
                          fontSize: "0.8rem",
                          fontWeight: 500,
                          borderRadius: 1,
                          padding: 1,
                          maxWidth: 220,
                          "& .MuiTooltip-arrow": {
                            color: color || "rgb(0 0 0 / 0%)"
                          }
                        }
                      }
                    }}
                    arrow
                    components={{arrow: "span"}}
                    placement="top"
                    disableHoverListener={!tooltipText || tooltipText === "No Event"}
                  >
                    <Box sx={{position: "relative", display: "inline-block"}}>
                      <Badge
                        overlap="circular"
                        color="secondary"
                        invisible={countForDay <= 1}
                        badgeContent={countForDay > 1 ? `+${countForDay - 1}` : null}
                        sx={{
                          "& .MuiBadge-badge": {
                            right: -4,
                            top: 0,
                            height: 18,
                            minWidth: 18,
                            fontSize: "0.65rem",
                            background: "#2b3a67"
                          }
                        }}
                      >
                        <PickersDay
                          {...props}
                          onClick={() => handleDayClick(props.day, type)}
                          sx={{
                            borderRadius: 2,
                            fontWeight: props.today ? "bold" : "normal",
                            backgroundColor: color
                              ? `${color}1f`
                              : props.today
                              ? "#1976D2 !important"
                              : undefined,
                            color: props.today ? "#fff" : color || undefined,
                            border: color
                              ? `1px solid ${color}`
                              : "1px solid #e0e0e0",
                            "&.Mui-selected": {
                              backgroundColor: props.today
                                ? "#1976D2 !important"
                                : "#9fdf38ff !important",
                              color: props.today ? "#fff" : "#fff"
                            },
                            width: "clamp(36px, 6.5vw, 54px)",
                            height: "clamp(36px, 6.5vw, 54px)",
                            m: "auto",
                            fontSize: {xs: "0.9rem", sm: "0.95rem"}
                          }}
                        />
                      </Badge>
                      {type === "conference" && countForDay > 0 && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 4,
                            left: 5,
                            right: 5,
                            display: "flex",
                            gap: "2px",
                            pointerEvents: "none"
                          }}
                        >
                          {map[dateNum].slice(0, 3).map((b, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                flex: 1,
                                height: 4,
                                borderRadius: 2,
                                bgcolor: getColorByType(b.conferenceType)
                              }}
                            />
                          ))}
                          {countForDay > 3 && (
                            <Box
                              sx={{
                                flexBasis: 12,
                                height: 4,
                                borderRadius: 2,
                                bgcolor: "#999"
                              }}
                            />
                          )}
                        </Box>
                      )}
                      {type === "event" && titleText && (
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 2,
                            left: 4,
                            right: 4,
                            textAlign: "center",
                            pointerEvents: "none"
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.58rem",
                              lineHeight: 1.1,
                              color: color || "#555",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis"
                            }}
                          >
                            {titleText}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Tooltip>
                );
              }
            }}
          />
        </LocalizationProvider>
      </Box>
    </Card>
  );

  const isAuthorized = canEditOrDelete(formData);
  const isReadOnly = viewOnly || !isAuthorized;

  return (
    <Box py={2} sx={{backgroundColor: "#f4f6f8", minHeight: "80vh"}}>
      <Grid container spacing={1.5}>
        <Grid item xs={12} lg={6}>
          {renderCard({
            title: "Calendar",
            buttonLabel: "Today",
            chips: [
              {label: "HOLIDAY", bg: "#5e35b1"},
              {label: "TRAINING", bg: "#2D90D5"},
              {label: "EVENT", bg: "#38C9F2"},
              {label: "WORKING", bg: "#43a047"}
            ],
            value: calendarValue,
            onChange: setCalendarValue,
            type: "event"
          })}
        </Grid>
        <Grid item xs={12} lg={6}>
          {renderCard({
            title: "Conference Booking",
            chips: [
              {label: "Conference Room", bg: "#00838f"},
              {label: "Conference-II", bg: "#5e35b1"},
              {label: "MULTI-UTILITY ROOM", bg: "#43a047"}
            ],
            value: conferenceValue,
            onChange: setConferenceValue,
            type: "conference"
          })}
        </Grid>
      </Grid>

      <Modal open={open} onClose={() => setOpen(false)} disableAutoFocus disableEnforceFocus>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "#fff",
            p: 2,
            borderRadius: 2,
            width: 500,
            boxShadow: 24
          }}
        >
          <IconButton
            onClick={() => setOpen(false)}
            sx={{position: "absolute", top: 8, right: 8}}
          >
            <CloseIcon />
          </IconButton>

          {formType === "event" ? (
            <>
              <Typography variant="h6" mb={2}>
                {isEditing ? "Edit Event" : "Add Event"}
              </Typography>
              <TextField
                fullWidth
                label="Title"
                value={formData.title}
                onChange={e => setFormData({...formData, title: e.target.value})}
                sx={{mb: 2}}
                required
              />
              <FormControl fullWidth sx={{mb: 2}}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={e => setFormData({...formData, type: e.target.value})}
                  label="Type"
                  sx={{height: "43px"}}
                >
                  {["HOLIDAY", "TRAINING", "EVENT", "WORKING"].map(type => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth sx={{mb: 2}}>
                <LocalizationProvider dateAdapter={CustomAdapterDayjs}>
                  <DatePicker
                    format="DD/MM/YYYY"
                    label="Start Date"
                    value={formData.startDate}
                    minDate={dayjs()}
                    onChange={newValue => {
                      if (formData.endDate && dayjs(newValue).isAfter(formData.endDate)) {
                        setFormData({...formData, startDate: newValue, endDate: null});
                      } else {
                        setFormData({...formData, startDate: newValue});
                      }
                    }}
                    renderInput={params => <TextField {...params} />}
                  />
                  <DatePicker
                    format="DD/MM/YYYY"
                    label="End Date"
                    sx={{mt: 2}}
                    value={formData.endDate}
                    minDate={formData.startDate || dayjs()}
                    onChange={newValue => {
                      if (
                        formData.startDate &&
                        dayjs(newValue).isBefore(formData.startDate)
                      ) {
                        alert("End date cannot be before start date");
                        return;
                      }
                      setFormData({...formData, endDate: newValue});
                    }}
                    renderInput={params => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </FormControl>
            </>
          ) : (
            <>
              <Typography variant="h6" mb={2} fontFamily={properties.fontFamily}>
                {viewOnly
                  ? "View Conference Booking"
                  : isEditing
                  ? "Edit Conference Booking"
                  : "Conference Booking"}
              </Typography>

              <FormControl fullWidth sx={{mb: 1}}>
                {formData && formData.conferenceId && (
                  <Box
                    sx={{
                      mb: 2,
                      p: 1.5,
                      backgroundColor: "#f5f9ff",
                      borderRadius: "8px",
                      border: "1px solid #d0e4ff",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      boxShadow: "0px 1px 3px rgba(0,0,0,0.05)"
                    }}
                  >
                    <PersonIcon sx={{color: "primary.main", mr: 1}} />
                    <Typography
                      fontFamily={properties.fontFamily}
                      variant="h6"
                      sx={{color: "#555", fontWeight: 500, whiteSpace: "nowrap"}}
                    >
                      Booked by:
                    </Typography>
                    <Typography
                      fontFamily={properties.fontFamily}
                      variant="h6"
                      sx={{
                        fontWeight: 500,
                        color: "#051c3fff",
                        textTransform: "capitalize"
                      }}
                    >
                      {formData.empName || "Unknown"}
                    </Typography>
                  </Box>
                )}
              </FormControl>

              <FormControl fullWidth sx={{mb: 2}}>
                <Stack spacing={1} direction="row">
                  {viewOnly ? (
                    <>
                      <Box
                        sx={{
                          width: "50%",
                          p: 1,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          backgroundColor: "#f9f9f9"
                        }}
                      >
                        <Typography variant="caption" color="textSecondary">
                          Start Date
                        </Typography>
                        <Typography variant="body1" sx={{fontWeight: 500, fontSize: "13px"}}>
                          {formData.startDate?.format("DD/MM/YYYY")}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: "50%",
                          p: 1,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          backgroundColor: "#f9f9f9"
                        }}
                      >
                        <Typography variant="caption" color="textSecondary">
                          End Date
                        </Typography>
                        <Typography variant="body1" sx={{fontWeight: 500, fontSize: "13px"}}>
                          {formData.endDate?.format("DD/MM/YYYY")}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <LocalizationProvider dateAdapter={CustomAdapterDayjs}>
                      <DatePicker
                        format="DD/MM/YYYY"
                        label="Start Date"
                        value={formData.startDate}
                        minDate={dayjs()}
                        onChange={newValue => {
                          if (
                            formData.endDate &&
                            dayjs(newValue).isAfter(formData.endDate)
                          ) {
                            setFormData({...formData, startDate: newValue, endDate: null});
                          } else {
                            setFormData({...formData, startDate: newValue});
                          }
                        }}
                        renderInput={params => <TextField {...params} />}
                      />
                      <DatePicker
                        format="DD/MM/YYYY"
                        label="End Date"
                        value={formData.endDate}
                        minDate={formData.startDate || dayjs()}
                        onChange={newValue => {
                          if (
                            formData.startDate &&
                            dayjs(newValue).isBefore(formData.startDate)
                          ) {
                            alert("End date cannot be before start date");
                            return;
                          }
                          setFormData({...formData, endDate: newValue});
                        }}
                        renderInput={params => <TextField {...params} />}
                      />
                    </LocalizationProvider>
                  )}
                </Stack>
              </FormControl>

              <FormControl fullWidth sx={{mb: 2}}>
                <Stack spacing={1} direction="row">
                  {viewOnly ? (
                    <>
                      <Box
                        sx={{
                          width: "50%",
                          p: 1,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          backgroundColor: "#f9f9f9"
                        }}
                      >
                        <Typography variant="caption" color="textSecondary">
                          Start Time
                        </Typography>
                        <Typography variant="body1" sx={{fontWeight: 500, fontSize: "13px"}}>
                          {formData.startTime?.format("HH:mm")}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          width: "50%",
                          p: 1,
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          backgroundColor: "#f9f9f9"
                        }}
                      >
                        <Typography variant="caption" color="textSecondary">
                          End Time
                        </Typography>
                        <Typography variant="body1" sx={{fontWeight: 500, fontSize: "13px"}}>
                          {formData.endTime?.format("HH:mm")}
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <MobileTimePicker
                        label="Start Time"
                        value={formData.startTime}
                        onChange={newValue => {
                          if (formData.endTime && newValue.isAfter(formData.endTime)) {
                            setTimeError("Start time cannot be after end time");
                          } else {
                            setTimeError("");
                          }
                          setFormData({...formData, startTime: newValue});
                        }}
                        sx={{width: "50%"}}
                      />
                      <MobileTimePicker
                        label="End Time"
                        value={formData.endTime}
                        onChange={newValue => {
                          if (
                            formData.startTime &&
                            newValue.isBefore(formData.startTime)
                          ) {
                            setTimeError("End time cannot be before start time");
                          } else {
                            setTimeError("");
                          }
                          setFormData({...formData, endTime: newValue});
                        }}
                        sx={{width: "50%"}}
                      />
                    </LocalizationProvider>
                  )}
                  {timeError && !viewOnly && (
                    <Typography color="error" variant="h6" sx={{mt: 1}}>
                      {timeError}
                    </Typography>
                  )}
                </Stack>
              </FormControl>

              <FormControl fullWidth sx={{mb: 2}}>
                {viewOnly ? (
                  <Box
                    sx={{
                      p: 1,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      backgroundColor: "#f9f9f9"
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      Meeting Title
                    </Typography>
                    <Typography variant="body1" sx={{fontWeight: 500, fontSize: "13px"}}>
                      {formData.title || "N/A"}
                    </Typography>
                  </Box>
                ) : (
                  <TextField
                    value={formData.title}
                    onChange={e => setFormData({...formData, title: e.target.value})}
                    label="Meeting Title"
                    sx={{height: "43px"}}
                  />
                )}
              </FormControl>

              <FormControl fullWidth sx={{mb: 2}}>
                {viewOnly ? (
                  <Box
                    sx={{
                      p: 1,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      backgroundColor: "#f9f9f9"
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      Conference Room
                    </Typography>
                    <Typography variant="body1" sx={{fontWeight: 500, fontSize: "13px"}}>
                      {formData.conferenceType || "N/A"}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <InputLabel>Conference Room</InputLabel>
                    <Select
                      value={formData.conferenceType}
                      onChange={e =>
                        setFormData({...formData, conferenceType: e.target.value})
                      }
                      label="Conference Room"
                      sx={{height: "43px"}}
                    >
                      {["Conference Room"].map(room => (
                        <MenuItem key={room} value={room}>
                          {room}
                        </MenuItem>
                      ))}
                    </Select>
                  </>
                )}
              </FormControl>

              <FormControl fullWidth sx={{mb: 2}}>
                {viewOnly ? (
                  <Box
                    sx={{
                      p: 1,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                      backgroundColor: "#f9f9f9"
                    }}
                  >
                    <Typography variant="caption" color="textSecondary">
                      Number of Attendees
                    </Typography>
                    <Typography variant="body1" sx={{fontWeight: 500, fontSize: "13px"}}>
                      {formData.noOfAttendee || "N/A"}
                    </Typography>
                  </Box>
                ) : (
                  <TextField
                    value={formData.noOfAttendee}
                    type="number"
                    onChange={e => {
                      const val = e.target.value;
                      if (!val.startsWith("-")) {
                        setFormData({...formData, noOfAttendee: val});
                      }
                    }}
                    label="Number of Attendees"
                    inputProps={{min: 1}}
                  />
                )}
              </FormControl>
            </>
          )}

          <Box mt={3} display="flex" justifyContent="flex-end">
            {isAuthorized && !isReadOnly && (
              <>
                <Button
                  variant="contained"
                  onClick={handleSubmit}
                  sx={{
                    color: "#ffffff",
                    mr: 1,
                    background: properties.colors.bgcolorbluethem
                  }}
                >
                  {isEditing ? "Update" : "Save"}
                </Button>
                {formType === "event" && isEditing && (
                  <Button
                    variant="outlined"
                    color="error"
                    sx={{mr: 2, color: "red", ml: 2}}
                    onClick={() => setConfirmDeleteOpen(true)}
                  >
                    Delete
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>
      </Modal>

      <Dialog
        open={openBookingList}
        onClose={() => setOpenBookingList(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{sx: {borderRadius: 2}}}
      >
        <DialogTitle sx={{fontWeight: 700}}>
          Conference Bookings for {selectedDate?.format("MMMM D, YYYY")}
        </DialogTitle>
        <DialogContent>
          {bookingList.length > 0 ? (
            <TableContainer
              component={Paper}
              sx={{borderRadius: 2, maxHeight: 400, width: "100%"}}
            >
              <Table size="medium" stickyHeader sx={{width: "100%"}}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{width: "40%"}}>Meeting Title</TableCell>
                    <TableCell sx={{width: "40%"}}>Conference Room</TableCell>
                    <TableCell sx={{width: "20%"}}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookingList.map(booking => (
                    <TableRow key={booking.conferenceId}>
                      <TableCell sx={{width: "50%"}}>{booking.meetingTitle}</TableCell>
                      <TableCell sx={{width: "30%"}}>{booking.conferenceType}</TableCell>
                      <TableCell sx={{width: "20%"}}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Tooltip title="View">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => handleViewBooking(booking, true)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {canEditOrDelete(booking) && (
                            <>
                              <Tooltip title="Edit">
                                <IconButton
                                  color="success"
                                  size="small"
                                  onClick={() => handleViewBooking(booking, false)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete">
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => handleDeleteClick(booking)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <DialogContentText>No bookings found for this date.</DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="info"
            sx={{color: "#ffffff", mr: 1, background: properties.colors.bgcolorbluethem}}
            onClick={handleAddNewBooking}
            disabled={selectedDate?.isBefore(dayjs().tz(dayjs.tz.guess()), "day")}
          >
            Add New Booking
          </Button>
          <Button variant="contained" color="info" onClick={() => setOpenBookingList(false)}>
            Back
          </Button>
        </DialogActions>
      </Dialog>

      {formType === "event" && (
        <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
          <DialogTitle sx={{fontWeight: 700, color: "#d32f2f"}}>
            Delete Calendar Booking
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{mb: 2}}>
              Are you sure you want to delete this calendar booking? This action cannot be
              undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="info"
              onClick={() => setConfirmDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleDeleteBooking} color="error" variant="contained">
              Delete Booking
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {formType === "conference" && (
        <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
          <DialogTitle
            sx={{fontWeight: 700, color: "#14212eff", fontFamily: properties.fontFamily}}
          >
            Delete Conference Booking
          </DialogTitle>
          <DialogContent>
            <Typography
              variant="body1"
              sx={{
                mb: 2,
                fontFamily: properties.fontFamily,
                fontSize: "16px",
                fontWeight: 500
              }}
            >
              Are you sure you want to delete this conference booking? This action cannot be
              undone.
            </Typography>
            <Box
              sx={{
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                backgroundColor: "#f9f9f9"
              }}
            >
              <Typography
                variant="h6"
                sx={{mb: 2, color: "#08396bff", fontFamily: properties.fontFamily}}
              >
                Booking Details
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Meeting Title</Typography>
                  <Typography variant="body1" sx={{fontWeight: 500, fontSize: "14px"}}>
                    {formData.title || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Conference Room</Typography>
                  <Typography variant="body1" sx={{fontWeight: 500, fontSize: "14px"}}>
                    {formData.conferenceType || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Start Date</Typography>
                  <Typography variant="body1" sx={{fontWeight: 500, fontSize: "14px"}}>
                    {formData.startDate?.format("DD/MM/YYYY")}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">End Date</Typography>
                  <Typography variant="body1" sx={{fontWeight: 500, fontSize: "14px"}}>
                    {formData.endDate?.format("DD/MM/YYYY")}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Start Time</Typography>
                  <Typography variant="body1" sx={{fontWeight: 500, fontSize: "14px"}}>
                    {formData.startTime?.format("HH:mm")}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">End Time</Typography>
                  <Typography variant="body1" sx={{fontWeight: 500, fontSize: "14px"}}>
                    {formData.endTime?.format("HH:mm")}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Number of Attendees
                  </Typography>
                  <Typography variant="body1" sx={{fontWeight: 500, fontSize: "14px"}}>
                    {formData.noOfAttendee || "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">Booked by</Typography>
                  <Typography variant="body1" sx={{fontWeight: 500, fontSize: "14px"}}>
                    {formData.empName || "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="info"
              onClick={() => setConfirmDeleteOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleDeleteBooking} color="error" variant="contained">
              Delete Booking
            </Button>
          </DialogActions>
        </Dialog>
      )}

      <Snackbar
        open={openSnabar}
        anchorOrigin={{vertical: "top", horizontal: "right"}}
        autoHideDuration={2000}
        onClose={handleSnabarClose}
      >
        <Alert
          onClose={handleSnabarClose}
          severity={snakColor === "success" ? "success" : "error"}
          variant="filled"
          sx={{
            backgroundColor:
              snakColor === "success" ? "green" : snakColor === "error" ? "red" : "blue",
            color: "#fff",
            width: "100%"
          }}
        >
          {snakMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ✅ React.memo prevents re-render when parent Dashboard state changes
export default React.memo(CustomCalendar);